import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useHints } from "@/contexts/HintsContext";

const ACCENT = "var(--accent-primary)";

/**
 * One-time contextual tooltip (onboarding.md Step 3). A small pill that shows
 * near a target element the first time a subscriber reaches an interaction.
 * Dismisses after 4s, or immediately when the parent calls markSeen on action.
 * Parent controls placement via className (needs a relative container) and
 * gates the trigger with the `active` prop.
 */
export const OnboardingHint = ({ hintKey, text, active = true, className = "" }) => {
  const { shouldShow, markSeen } = useHints();
  const canShow = active && shouldShow(hintKey);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!canShow) { setVisible(false); return undefined; }
    setVisible(true);
    const t = setTimeout(() => { setVisible(false); markSeen(hintKey); }, 4000);
    return () => clearTimeout(t);
  }, [canShow, hintKey, markSeen]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.2 }}
          data-testid={`hint-${hintKey}`}
          className={`pointer-events-none absolute z-[60] ${className}`}
        >
          <div
            className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
            style={{ background: ACCENT, color: "#fff", boxShadow: "0 6px 20px color-mix(in srgb, var(--accent-primary) 40%, transparent)" }}
          >
            {text}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnboardingHint;
